import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";

import { PageWrapper } from "../../components/PageWrapper";
import { PastShiftDetailsHeader } from "./components/PastShiftDetailsHeader";
import { PastShiftOptionsBottomSheet } from "./components/PastShiftOptionsBottomSheet";
import { type MissedPunchRequestStatus } from "./types";

interface PastShiftDetailsPageProps {
  data: {
    shift: WorkerShift;
    missedPunchRequestStatus: MissedPunchRequestStatus;
    isFullShiftPayment: boolean;
    requestedAmountInDollars?: number;
    totalPayInDollars: number;
    hourlyRateInDollars: number;
  };
}

export function PastShiftDetailsPage(props: PastShiftDetailsPageProps) {
  const { data } = props;
  const {
    shift,
    missedPunchRequestStatus,
    isFullShiftPayment,
    requestedAmountInDollars,
    totalPayInDollars,
    hourlyRateInDollars,
  } = data;

  const optionsModalState = useModalState();

  useLogEffect(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_PAGE_VIEWED, {
    shiftId: shift._id,
  });

  const dateLabel = formatShortDateWithWeekday(shift.start, {
    timeZone: shift.facility.tmz ?? DEFAULT_TIMEZONE,
  });
  const qualificationName = shift.agentReq;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PastShiftDetailsHeader
          shiftId={shift._id}
          dateLabel={dateLabel}
          qualificationName={qualificationName}
          missedPunchRequestStatus={missedPunchRequestStatus}
          isVerified={shift.verified ?? false}
          totalPayInDollars={totalPayInDollars}
          hourlyRateInDollars={hourlyRateInDollars}
          requestedAmountInDollars={requestedAmountInDollars}
          isFullShiftPayment={isFullShiftPayment}
          onOpenOptions={() => {
            optionsModalState.openModal();
          }}
        />
        {/* Placeholder for future shift detail content cards */}
        <Box sx={{ flexGrow: 1 }} />
        <PastShiftOptionsBottomSheet modalState={optionsModalState} shiftId={shift._id} />
      </PageWrapper>
    </ThemeProvider>
  );
}
